/**
 * Guest Monkey slips: build stays 100% client-side (no API). flushPendingGuestMonkeyChallenge
 * runs only after the user has a real session — that is the first server write.
 */

import axios from 'axios';
import { API_URL } from '../../../constants';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { logger } from '../../helpers/logger.helper';
import {
  validateChallengeInputs,
  resolveFriendUserId,
  buildFriendParticipantIds,
  extractMatchIds,
  buildPicksData,
  buildChallengeData,
} from './createChallenge.helper';
import { generateChallengeName } from './createDataBuilders';
import type {
  PendingGuestMonkeyChallengePayload,
  PendingGuestMatchDisplay,
} from '../../shared/stores/pendingGuestMonkeyChallengeStore';
import type { Game } from '../types/create.types';
import { GUEST_MONKEY_SYNTHETIC_FRIEND } from '../constants/guestMonkeySyntheticFriend.constants';
import { computeLatestEstimatedEndMsFromGames } from './guestMonkeyExpiry.helper';
import { parseKickoffFromDisplay } from './createGames.helper';

const buildAuthHeaders = (token: string | null | undefined) => ({
  'Content-Type': 'application/json',
  ...(token && { Authorization: `Bearer ${token}` }),
});

function buildMatchDisplayByIdFromGames(
  matchIds: string[],
  games: Game[],
): Record<string, PendingGuestMatchDisplay> {
  const map: Record<string, PendingGuestMatchDisplay> = {};
  for (const id of matchIds) {
    const g = games.find((x) => x.id === id);
    if (!g) {
      map[id] = {
        homeTeam: 'Home',
        awayTeam: 'Away',
        league: '',
        homeOdd: 2,
        drawOdd: 3,
        awayOdd: 2,
      };
      continue;
    }
    const kickoff = parseKickoffFromDisplay(g);
    map[id] = {
      homeTeam: g.homeTeam,
      awayTeam: g.awayTeam,
      league: g.league,
      dateIso: kickoff ? kickoff.toISOString() : undefined,
      homeKit: g.homeKit,
      awayKit: g.awayKit,
      // A FIELD sport has no home/away price; undefined is this snapshot's "absent" value.
      homeOdd: g.odds.home ?? undefined,
      drawOdd: g.odds.draw,
      awayOdd: g.odds.away ?? undefined,
      ...(g.oddsMarkets != null && g.oddsMarkets.length > 0 ? { oddsMarkets: g.oddsMarkets } : {}),
    };
  }
  return map;
}

/** Stable id for each queued guest slip (Arena cards + flush order). */
export function createGuestPendingLocalId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `g_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function buildPendingGuestMonkeyChallengePayload(
  pickSlips: Parameters<typeof extractMatchIds>[0],
  games: Game[],
  selectedFriendId: string | null,
  selectedGroupId: string | null,
  stakeCoins: number | undefined,
):
  | { ok: true; payload: Omit<PendingGuestMonkeyChallengePayload, 'localId'> }
  | { ok: false; error: string } {
  if (selectedGroupId) {
    return { ok: false, error: 'Guest play is only available vs the Monkey.' };
  }
  if (selectedFriendId !== MONKEY_USER_ID) {
    return { ok: false, error: 'Select the Monkey to send your challenge.' };
  }

  const friend = GUEST_MONKEY_SYNTHETIC_FRIEND;
  const validation = validateChallengeInputs(
    selectedFriendId,
    selectedGroupId,
    pickSlips,
    friend,
    selectedFriendId,
  );
  if (!validation.valid) {
    return {
      ok: false,
      error: validation.error ?? 'Add at least one game and pick the Monkey.',
    };
  }

  const resolved = resolveFriendUserId(friend, selectedFriendId);
  if (resolved.error || !resolved.userId) {
    return {
      ok: false,
      error: resolved.error?.message ?? 'Could not use the Monkey for this challenge.',
    };
  }

  const matchIds = [...new Set(extractMatchIds(pickSlips))];
  const picks = buildPicksData(pickSlips, games);
  const challengeName = generateChallengeName(games, pickSlips, [friend]);
  const latestEstimatedEndMs = computeLatestEstimatedEndMsFromGames(matchIds, games);
  const matchDisplayById = buildMatchDisplayByIdFromGames(matchIds, games);

  return {
    ok: true,
    payload: {
      challengeName,
      opponentUserId: resolved.userId,
      matchIds,
      picks,
      stakeCoins: stakeCoins ?? 0,
      matchDisplayById,
      ...(latestEstimatedEndMs != null ? { latestEstimatedEndMs } : {}),
    },
  };
}

/** POST /challenges — call only when authenticated; never for guest browsing. */
export async function flushPendingGuestMonkeyChallenge(
  payload: PendingGuestMonkeyChallengePayload,
  creatorUserId: string,
  token: string | null,
): Promise<void> {
  const participantIds = buildFriendParticipantIds(creatorUserId.trim(), payload.opponentUserId);
  const challengeData = buildChallengeData(
    creatorUserId.trim(),
    payload.challengeName,
    participantIds,
    payload.matchIds,
    undefined,
    undefined,
    payload.picks.length > 0 ? payload.picks : undefined,
    payload.stakeCoins,
  );

  const { data } = await axios.post<{ success: boolean }>(`${API_URL}/challenges`, challengeData, {
    headers: buildAuthHeaders(token),
  });
  if (!data?.success) {
    logger.error('[flushPendingGuestMonkeyChallenge] API returned success: false');
    throw new Error('Challenge was not created');
  }
}
